import { ReactNode, useId, useMemo, useState } from 'react';
import { SvgIcon } from '../../components/icons';
import { sortRowsByKey, SortDirection } from '../../utils/tableSort';
import { EmptyState } from './EmptyState';
import { IconButton } from './Button';

export type Column<T> = {
  /** Sort key — must be a real field name on the row. */
  key: string;
  header: string;
  sortable?: boolean;
  align?: 'left' | 'right';
  /**
   * Controls which columns survive a narrow viewport. `low` drops below
   * 1200px, `medium` below 900px. Dropping columns beats forcing a 16-column
   * horizontal scroll on a tablet.
   */
  priority?: 'high' | 'medium' | 'low';
  /** Pins the column to the left edge while the rest scrolls. */
  sticky?: boolean;
  render: (row: T, index: number) => ReactNode;
  /** Cell content for the totals row, if the table has one. */
  total?: ReactNode;
};

type DataTableProps<T> = {
  columns: Array<Column<T>>;
  rows: T[];
  getRowKey: (row: T, index: number) => string | number;
  /** Renders a sticky totals row when any column defines `total`. */
  showTotals?: boolean;
  pageSize?: number;
  emptyTitle?: string;
  emptyHint?: string;
  emptyAction?: ReactNode;
  caption?: string;
};

const PAGE_SIZES = [25, 50, 100];

/**
 * Dense, sortable, paginated table.
 *
 * Three things it does that the production `Table` does not:
 *
 * 1. **Sortable headers are real `<button>`s carrying `aria-sort`.** The
 *    production table sorts from a bare `<th onClick>` — unreachable by
 *    keyboard and silent to assistive tech.
 * 2. **The first column can stick.** The Warranty screen is 16 columns wide;
 *    without this you lose the tail number as soon as you scroll right.
 * 3. **Row height comes from `--ds-row-h`** (32px compact vs the current ~53px),
 *    so density is a token change rather than a rewrite.
 *
 * Sorting reuses `src/utils/tableSort` so the demo orders rows exactly the way
 * the real app does, including its date-string handling.
 */
export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  showTotals = false,
  pageSize: initialPageSize = 25,
  emptyTitle = 'Nothing to show',
  emptyHint,
  emptyAction,
  caption,
}: DataTableProps<T>) {
  const [sort, setSort] = useState<{ key: string; dir: SortDirection } | null>(
    null,
  );
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const pageSizeId = useId();

  // Derived, not state: keeping the sorted order as state means a parent
  // re-render silently discards the user's chosen sort.
  const sorted = useMemo(
    () => (sort ? sortRowsByKey(rows, sort.key, sort.dir) : rows),
    [rows, sort],
  );

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const visible = useMemo(
    () => sorted.slice(safePage * pageSize, safePage * pageSize + pageSize),
    [sorted, safePage, pageSize],
  );

  const toggleSort = (key: string) =>
    setSort((prev) =>
      prev?.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' },
    );

  const ariaSort = (key: string): 'ascending' | 'descending' | 'none' =>
    sort?.key !== key ? 'none' : sort.dir === 'asc' ? 'ascending' : 'descending';

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<SvgIcon name="search" size={20} />}
        title={emptyTitle}
        hint={emptyHint}
        action={emptyAction}
      />
    );
  }

  const from = safePage * pageSize + 1;
  const to = Math.min(sorted.length, (safePage + 1) * pageSize);

  return (
    <div>
      <div className="ds-table-wrap ds-scroll">
        <table className="ds-table">
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  scope="col"
                  data-ds-sticky={c.sticky ? 'true' : undefined}
                  data-ds-priority={c.priority}
                  aria-sort={c.sortable ? ariaSort(c.key) : undefined}
                  style={c.align === 'right' ? { textAlign: 'right' } : undefined}
                >
                  {c.sortable ? (
                    <button
                      type="button"
                      className="ds-th-btn"
                      onClick={() => toggleSort(c.key)}
                      style={
                        c.align === 'right'
                          ? { justifyContent: 'flex-end' }
                          : undefined
                      }
                    >
                      {c.header}
                      <span className="ds-th-arrow" aria-hidden="true">
                        <SvgIcon
                          name={
                            sort?.key === c.key && sort.dir === 'desc'
                              ? 'arrowDownward'
                              : 'arrowUpward'
                          }
                          size={11}
                        />
                      </span>
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {visible.map((row, i) => (
              <tr
                key={getRowKey(row, i)}
                data-ds-zebra={i % 2 === 1 ? 'true' : undefined}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    data-ds-sticky={c.sticky ? 'true' : undefined}
                    data-ds-priority={c.priority}
                    className={c.align === 'right' ? 'ds-td-num' : undefined}
                  >
                    {c.render(row, i)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>

          {showTotals && (
            <tfoot>
              <tr>
                {columns.map((c) => (
                  <td
                    key={c.key}
                    data-ds-sticky={c.sticky ? 'true' : undefined}
                    data-ds-priority={c.priority}
                    className={c.align === 'right' ? 'ds-td-num' : undefined}
                  >
                    {c.total ?? ''}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="ds-pagination">
        {/* A real <label for>, unlike the production table's bare <span>. */}
        <label htmlFor={pageSizeId}>Rows</label>
        <select
          id={pageSizeId}
          className="ds-field"
          style={{ width: 68, height: 24 }}
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(0);
          }}
        >
          {PAGE_SIZES.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>

        <span className="ds-num">
          {from}–{to} of {sorted.length}
        </span>

        <div className="flex items-center gap-1">
          <IconButton
            label="Previous page"
            size="sm"
            disabled={safePage === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            <SvgIcon name="keyboardArrowLeft" size={16} />
          </IconButton>
          <IconButton
            label="Next page"
            size="sm"
            disabled={safePage >= pageCount - 1}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
          >
            <SvgIcon name="keyboardArrowRight" size={16} />
          </IconButton>
        </div>
      </div>
    </div>
  );
}
